import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import "./email-template-list.scss";
import EmailLetterFilters from "../email-letter-filters";
import "../email-letter-templates.scss";
import _ from "lodash";
import { MUITable } from "../../../common/components";
import { Checkbox, Button } from "@mui/material";
import { PaginationComponent } from "../../../common/components";
import {
  actionOptions,
  formattedFolderNameFilterOptions,
  emailTemplateTableHeaders,
} from "../email-letter-helper";
import EmailLetterMassActions from "../email-letter-mass-actions/email-letter-mass-actions";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import {
  getEmailLetterTemplates,
  getFoldersList,
  moveTemplateToFolder,
  deleteSingleTemplate,
  getTemplateHeaderOrFooter
} from "../../../api/email-letter-templates";
import { AlertComponent } from "../../../common/components";
import MoveTemplateToFolderPopup from "../email-letter-mass-actions/move-to-folder-popup";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { EmailTemplatesListProps } from "../email-letter-types";
import { routes } from "../../../constants/routes";
import { convertUTCToLocal } from "../../../utils/utils";
import { ConfirmationPopup } from "../../../common/components";
import ComposeEmailDialog from "../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/compose-email/compose-email";
import NewEmailTemplateEditor from "./new-email-template-editor/new-email-template-editor";
import EmailSetUpBanner from "../../../common/components/email-setup-required-banner/email-setup-banner";
import { useLogin } from "../../../common/hooks/useUser";

const EmailTemplateList: React.FC<EmailTemplatesListProps> = ({
  viewOpen,
  setViewOpen,
  crumbMappingOption,
  setCrumbMappingOption,
  templateType = "Email",
  isShowCreateTemplate = false,
  setShowCreateTemplateOpen,
  setIsLetterPrintPreviewOpen,
  setLetterTemplatePdfUrl,
  isLoading,
  setIsLoading,
  addNewTemplateBreadcrumb,
  setAddNewTemplateBreadcrumb,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0); // Set an initial value for testing
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [searchText, setSearchText] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<any>({
    open: false,
    message: "",
    type: "info",
  });
  const searchRef = useRef<{ clear: () => void }>(null);
  const [selectedEmailTemplatesMap, setSelectedEmailTemplatesMap] = useState<{
    [page: number]: number[];
  }>({});
  const [activeFilters, setActiveFilters] = useState<any>({});
  const isSearchTriggered = useRef(false);
  const [folderPopupOpen, setFolderPopupOpen] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [selectedTemplateName, setSelectedTemplateName] = useState<
    string | null
  >(null);
  const [folderOptions, setFolderOptions] = useState<any[]>([
    { id: "all", folder_name: "View All" },
    { id: "notInFolder", folder_name: "(Not in a folder)" },
  ]);
  const [deleteTemplateOpen, setDeleteTemplateOpen] = useState<boolean>(false);
  const [shareTemplateOpen, setShareTemplateOpen] = useState<boolean>(false);
  const moduleName =
    templateType === "Email"
      ? MODULES.EMAIL_TEMPLATES
      : MODULES.LETTER_TEMPLATES;
  const { canRead, canWrite, canDelete } = useResourcePermissions(moduleName);
  const [currentTemplateData, setCurrentTemplateData] = useState<any>(null);
  const loginDetails = useLogin();
  const isEmailConfigured = loginDetails?.isEmailConfigured;
  const isSuperAdmin = loginDetails?.userDetails?.roles?.role === "Super Admin";
  const [templateMetaData, setTemplateMetaData] = useState<any>(null);

  const isAllSelected =
    data.length > 0 &&
    data.every((row) =>
      selectedEmailTemplatesMap[currentPage]?.includes(row.id)
    ) &&
    selectedEmailTemplatesMap[currentPage]?.length > 0;

  const isIndeterminate =
    selectedEmailTemplatesMap[currentPage]?.length > 0 &&
    selectedEmailTemplatesMap[currentPage]?.length < data.length;

  const handleSelectAll = () => {
    setSelectedEmailTemplatesMap((prev) => {
      const currentPageLeads = data.map((item) => item.id);
      const selectedLeadsOnPage = prev[currentPage] || [];

      if (selectedLeadsOnPage.length > 0) {
        return { ...prev, [currentPage]: [] };
      }
      return { ...prev, [currentPage]: currentPageLeads };
    });
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedEmailTemplatesMap((prev) => {
      const prevSelected = prev[currentPage] || [];
      const newSelected = prevSelected.includes(id)
        ? prevSelected.filter((leadId) => leadId !== id)
        : [...prevSelected, id];

      return {
        ...prev,
        [currentPage]: newSelected,
      };
    });
  };

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on search
      setSearchText(text);
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        const templateName = item?.name || "-";
        const folder = item?.folder?.folder_name || "-";
        const emailSubjectLine = item?.subject || "-";
        const description = item?.subject || "-";
        const lastEdited = item?.updated_at
          ? convertUTCToLocal(item.updated_at, "MM-DD-YYYY hh:mm A")
          : "-";
        return {
          id: item.id,
          templateName,
          folder,
          emailSubjectLine,
          description,
          lastEdited,
          originalItem: item,
        };
      });
    }
    return [];
  }, [data]);

  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading Templates...";
    }
    if (searchText) {
      return "No templates found matching your search criteria.";
    }
    if (activeFilters?.folderId && activeFilters?.folderId !== "all") {
      return `No templates found in this folder.`;
    }
    return "No Email Templates found";
  }, [searchText, isLoading, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const headers = [
    {
      key: "select",
      label: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleSelectAll}
        />
      ),
    },
    ...emailTemplateTableHeaders(templateType),
  ];

  const handleAction = (actionType: string, id: number) => {
    setSelectedTemplateId(id);
    if (actionType === "Archive") {
      setDeleteTemplateOpen(true);
    }
    if (actionType === "Share") {
      setShareTemplateOpen(true);
      setCurrentTemplateData(data?.find((item) => item.id === id));
    }
  };

  const fetchEmailTemplates = async (
    page = 1,
    search: string = "",
    filters: any = {}
  ) => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (filters?.folderId) {
        if (filters.folderId !== "all" && filters.folderId !== "notInFolder") {
          params.folderId = Number(filters.folderId);
        }
        if (filters.folderId === "notInFolder") {
          params.notInFolder = true;
        }
      }
      if (search) {
        params.searchText = search;
      }
      params.templateType = templateType;
      const response = await getEmailLetterTemplates(params);
      setData(response.data.data.result);
      setTotalRecords(response.data.data.totalRecords);
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    } catch (error: any) {
      console.error("Error fetching email templates:", error);

      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message || "Failed to fetch email templates.",
        type: "error",
      });

      setIsLoading(false);
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchEmailTemplates(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters?.folderId]);

  const handleViewClick = async (
    templateId: number,
    templateName: string,
    template: any
  ) => {
    setSelectedTemplateId(templateId);
    setSelectedTemplateName(templateName);

    if (crumbMappingOption && setCrumbMappingOption) {
      setCrumbMappingOption([
        {
          label: `${templateType} Templates`,
          link:
            templateType === "Email"
              ? routes.EMAILTEMPLATES
              : routes.LETTERTEMPLATES,
          handleClick: (event: any) => {
            event.preventDefault();
            setCrumbMappingOption([...crumbMappingOption]);
            setSelectedTemplateId(null);
            setSelectedTemplateName('');
            setViewOpen({ isOpen: false, mode: "view", template: null });
            fetchTemplatesIncludingSearchAndFilters();
          },
        },
        {
          label: templateName,
          link: "",
        },
      ]);
    }
    setViewOpen({
      isOpen: true,
      mode: "edit",
      template: template?.originalItem || template,
    });
  };

  const fetchFoldersList = async () => {
    try {
      const response = await getFoldersList({ templateType: templateType });
      if (response?.data?.success === true) {
        const folders = response?.data?.data?.map((folder: any) => ({
          id: folder.id,
          folder_name: folder.folder_name,
        }));
        setFolderOptions([
          { id: "all", folder_name: "View All" },
          { id: "notInFolder", folder_name: "(Not in a folder)" },
          ...folders,
        ]);
      }
    } catch (error: any) {
      console.error("Error fetching folders:", error);

      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Failed to fetch folders.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchFoldersList();
    setAddNewTemplateBreadcrumb?.([{
      label: "Email Templates",
      link: routes.EMAILTEMPLATES,
      handleClick: (event: any) => {
        event.preventDefault();
        setShowCreateTemplateOpen?.(false);
        callFetchTemplates();
        fetchFoldersList();
      },
    },
    {
      label: "Add Email Template",
      link: "#",
    },
    ])
    fetchTemplateHeaderFooter();
  }, []);

  const callFetchTemplates = () => {
    if (searchText) resetSearch();
      setActiveFilters((prev: any) => ({
       folderName: "View All",
      folderId: "all",
      }));
    
    if (currentPage === 1) fetchEmailTemplates();
    else setCurrentPage(1);
  };

  const moveTemplateAPI = async (
    data: {
      templateIds: number[];
      folderId: string;
      templateType: string;
      notInAFolder: boolean;
    },
    folderName: string
  ) => {
    setIsLoading(true);
    try {
      const payload = { ...data };
      if (data?.folderId === "notInFolder") {
        delete (payload as { folderId?: string }).folderId;
        payload.notInAFolder = true;
      }
      await moveTemplateToFolder(payload);
      setAlertMessage({
        open: true,
        message: `Selected ${data?.templateIds.length === 1 ? 'template' : 'templates'} moved to ${folderName} successfully.`,
        type: "success",
      });
      fetchTemplatesIncludingSearchAndFilters();
      setSelectedEmailTemplatesMap({});
    } catch (error: any) {
      console.error("Error moving template:", error);
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Failed to move template.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateMove = (folder: { id: string; folder_name: string }) => {
    if (selectedTemplateId) {
      const templateIds = [selectedTemplateId];
      if (templateIds.length > 0) {
        moveTemplateAPI(
          {
            templateIds,
            folderId: folder.id,
            templateType: templateType,
            notInAFolder: false,
          },
          folder.folder_name
        );
      } else {
        setAlertMessage({
          open: true,
          message: "No templates selected to move.",
          type: "error",
        });
      }
    }
  };

  const deleteSingleTemplateApi = async (id: number) => {
    setIsLoading(true);
    try {
      const response = await deleteSingleTemplate(id, {
        templateType: templateType,
      });
      if (response?.data?.success === true) {
        setAlertMessage({
          open: true,
          message: `${templateType} template archived successfully.`,
          type: "success",
        });
        fetchTemplatesIncludingSearchAndFilters();
      }
    } catch (error: any) {
      console.error("Error deleting template:", error);
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Failed to delete template.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    if (selectedTemplateId) {
      deleteSingleTemplateApi(selectedTemplateId);
      setDeleteTemplateOpen(false);
    }
  };

  const handleOnCloseEditor = () => {
    setViewOpen({
      isOpen: false,
      mode: "view",
      template: null,
    });
    fetchTemplatesIncludingSearchAndFilters();
    setIsLoading(false);
    setSelectedTemplateName('');
    setSelectedTemplateId(null);
    if(crumbMappingOption && setCrumbMappingOption) {
      setCrumbMappingOption([
        {
          label: `${templateType} Templates`,
          link:
            templateType === "Email"
              ? routes.EMAILTEMPLATES
              : routes.LETTERTEMPLATES,
            handleClick: (event: any) => {
            event.preventDefault();

          },
        },
      ]);
    }
  };
  const filteredFolderOptions = useMemo(() => {
    return folderOptions.filter((option) => option.id !== "all");
  }, [folderOptions]);

    const handleEmailSuccess = () => {
      setAlertMessage({
        open: true,
        message: `Email Sent Successfully.`,
        type: "success",
      });
    setShareTemplateOpen(false);
  };
  
  const fetchTemplatesIncludingSearchAndFilters = () => {
    if (currentPage === 1) {
      fetchEmailTemplates(1, searchText || "", activeFilters);
    } else {
      setCurrentPage(1);
    }
  }

  const fetchTemplateHeaderFooter = async () => {
      try {
        const headerResponse = await getTemplateHeaderOrFooter();
        if(headerResponse?.data?.data)
          setTemplateMetaData(headerResponse.data.data);
      } catch (error:any) {
        setAlertMessage({
          open: true,
          message: error?.response?.data?.message || "Failed to fetch header, footer, opt-out of email templates.",
          type: "error",
        });
      }
  };


  return (
    <>
      <div
        className="filters-and-list-wrapper email-letter-templates-filters-and-list-block-container"
        style={{ display: viewOpen?.isOpen ? "none" : "block" }}
      >
        <div className="templates-list-wrapper">
          <EmailLetterFilters
            onSearch={handleSearch}
            searchRef={searchRef}
            viewAllOptions={formattedFolderNameFilterOptions(folderOptions)}
            setActiveFilters={setActiveFilters}
            activeFilters={activeFilters}
            templateType={templateType}
            isShowCreateTemplate={isShowCreateTemplate}
            setShowCreateTemplateOpen={setShowCreateTemplateOpen}
            fetchTemplatesList={fetchTemplatesIncludingSearchAndFilters}
            setLoading={setIsLoading}
          />
          {!isEmailConfigured && isSuperAdmin && templateType === "Email" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
          {
        
            selectedEmailTemplatesMap[currentPage] &&
            selectedEmailTemplatesMap[currentPage].length > 0 && (
              <EmailLetterMassActions
                selectedEmailTemplatesMap={selectedEmailTemplatesMap}
                setSelectedEmailTemplatesMap={setSelectedEmailTemplatesMap}
                folderOptions={filteredFolderOptions}
                fetchFoldersList={fetchFoldersList}
                moveTemplateAPI={moveTemplateAPI}
                setAlertMessage={setAlertMessage}
                // setAlertType={setAlertType}
                // setIsAlertOpen={setAlertOpen}
                setLoading={setIsLoading}
                fetchTemplatesList={fetchTemplatesIncludingSearchAndFilters}
                templateType={templateType}
              />
            )}
          <div className="lead-list-table-filter">
            <div
              className={`lead-list-table
                ${templateType === "Email" ? "is-email" : "is-letter"}
                ${selectedEmailTemplatesMap?.[currentPage]?.length > 0 ? "has-templates" : "no-templates"}
              `}
              // style={{
              //   height:
              //     templateType === "Email"
              //       ? !isEmailConfigured
              //         ? selectedEmailTemplatesMap?.[currentPage]?.length > 0 ? "calc(100vh - 426px)" : "calc(100vh - 354px)" //extra email setup banner height 80px so 274+80=354px and 346+80=426px
              //         : selectedEmailTemplatesMap?.[currentPage]?.length > 0
              //           ? "calc(100vh - 346px)" //breadcrumb(63px) + tabs(48px) + tabs margin bottom (15px) + filters(80px) + action buttons 72px + pagination(68px) = 274px
              //           : "calc(100vh - 274px)" //breadcrumb(63px) + tabs(48px) + tabs margin bottom (15px) + filters(80px) + pagination(68px) = 274px
              //       : selectedEmailTemplatesMap?.[currentPage]?.length > 0
              //         ? "calc(100vh - 292px)"
              //         : "calc(100vh - 220px)",
              // }}
            >
              <MUITable
                headers={headers}
                data={formattedData.map((item) => ({
                  id: item.id,
                  key: item.id,
                  select: (
                    <Checkbox
                      checked={
                        selectedEmailTemplatesMap[currentPage]?.includes(
                          item.id
                        ) || false
                      }
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleCheckboxChange(item.id)}
                    />
                  ),
                  templateName: item.templateName,
                  folder: (
                    <>
                      <Button
                        variant="text"
                        endIcon={<KeyboardArrowDownIcon />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setFolderPopupOpen(true);
                          setSelectedTemplateId(item.id);
                        }}
                        sx={{
                          textTransform: "none",
                          padding: "4px 8px",
                          minWidth: "auto",
                          color: "inherit",
                          fontWeight: "normal",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        {item.folder}
                      </Button>
                    </>
                  ),
                  emailSubjectLine: item.emailSubjectLine,
                  description: item?.description,
                  lastEdited: item.lastEdited,
                  action: "verticalEllipse",
                  originalItem: item.originalItem,
                }))}
                noResultsTxt={noResultsTxt}
                fieldName="emailTemplates"
                actionOptions={actionOptions(
                  { canRead, canWrite, canDelete },
                  templateType, isEmailConfigured
                )}
                handleAction={handleAction}
                onRowClick={(rowData: any) =>
                  handleViewClick(rowData?.key, rowData?.templateName, rowData)
                }
              />
            </div>
            <MoveTemplateToFolderPopup
              isOpen={folderPopupOpen}
              onClose={() => {
                setFolderPopupOpen(false)
              }}
              onMove={(folder) => {
                setFolderPopupOpen(false);
                handleTemplateMove(folder);
              }}
              folders={filteredFolderOptions}
              templateType={templateType}
              fetchFoldersList={fetchFoldersList}
              currentAssignedFolderName={
                data?.find((item) => item.id === selectedTemplateId)
                  ?.folder?.folder_name || "(Not in a folder)"
              }
            />
            <ConfirmationPopup
              open={deleteTemplateOpen}
              title={"Archive Template"}
              buttonText="Archive"
              note={`Are you sure you want to archive this template? This action cannot be undone.`}
              onCancel={() => setDeleteTemplateOpen(false)}
              onOkay={handleDelete}
            />
            <ComposeEmailDialog
              open={shareTemplateOpen}
              onClose={() => setShareTemplateOpen(false)}
              templateData={currentTemplateData}
              setSelectedTemplateData={setCurrentTemplateData}
              onSuccess={handleEmailSuccess}
              templateMetaData={templateMetaData}
            />
          </div>
        </div>
        {totalRecords && totalRecords > 0 ? (
          <div className="leads-pagination-container">
            <PaginationComponent
              totalRecords={totalRecords}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {viewOpen?.isOpen && (
        <div className="template-list-view-edit-wrapper">
          <NewEmailTemplateEditor
            onClose={handleOnCloseEditor}
            setAlertMessage={setAlertMessage}
            toggleEditor={viewOpen}
            setToggleEditor={setViewOpen}
            setViewOpen={setViewOpen}
            setIsLoading={setIsLoading}
            templateType={templateType}
            crumbMappingOption={crumbMappingOption}
            setCrumbMappingOption={setCrumbMappingOption}
            setIsLetterPrintPreviewOpen={setIsLetterPrintPreviewOpen}
            setLetterTemplatePdfUrl={setLetterTemplatePdfUrl}
          />
        </div>
      )}
      <AlertComponent
        message={alertMessage?.message}
        type={alertMessage?.type || "info"}
        open={alertMessage?.open || false}
        onClose={() => setAlertMessage({ ...alertMessage, open: false })}
      />
    </>
  );
};

export default EmailTemplateList;
